import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { formatCurrency } from '@/common/utils/formatters'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useUpdateGovernmentAllocationApprovedValue } from '@/features/privateCompanies/hooks/useUpdateGovernmentAllocationApprovedValue'
import { GovernmentAllocationInfoItem } from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Show/components/GovernmentAllocationInfoItem'
import {
  updateGovernmentAllocationApprovedValueSchema,
  UpdateGovernmentAllocationApprovedValueSchema,
} from '@/features/privateCompanies/schemas/updateGovernmentAllocationApprovedValue.schema'
import { CompanyGovernmentAllocationData } from '@/features/privateCompanies/types/companyGovernmentAllocation.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { Dispatch, SetStateAction, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

interface UpdateGovernmentAllocationApprovedValueModalProps {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  companyGovernmentAllocation: CompanyGovernmentAllocationData
}

export const UpdateGovernmentAllocationApprovedValueModal = ({
  isOpen,
  setIsOpen,
  companyGovernmentAllocation,
}: UpdateGovernmentAllocationApprovedValueModalProps) => {
  const { id: governmentAllocationId } = useParams()
  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
    setError,
  } = useForm<UpdateGovernmentAllocationApprovedValueSchema>({
    resolver: zodResolver(updateGovernmentAllocationApprovedValueSchema),
    defaultValues: {
      approved_value: '',
    },
  })

  const onSubmit: SubmitHandler<UpdateGovernmentAllocationApprovedValueSchema> = (data) => {
    updateGovernmentAllocationApprovedValue(data)
  }

  const onSuccess = () => {
    resetForm()
    queryClient.invalidateQueries({ queryKey: ['companyGovernmentAllocation', governmentAllocationId] })
    setIsOpen(false)
  }

  const onError = (error: any) => {
    if (error.response?.data.errors.base) {
      setMessages(error.response?.data.errors.base)
    } else {
      FormTransformers.errorsTransformer({ setError })(error)
    }
  }

  const {
    mutate: updateGovernmentAllocationApprovedValue,
    isPending: updateGovernmentAllocationApprovedValueIsPending,
    isError: updateGovernmentAllocationApprovedValueIsError,
  } = useUpdateGovernmentAllocationApprovedValue({
    governmentAllocationId,
    onSuccess,
    onError,
  })

  return (
    <>
      <Modal
        isOpen={isOpen && !messages}
        setIsOpen={(value) => {
          if (!value) {
            setTimeout(() => resetForm({ approved_value: '' }), 300)
          }

          setIsOpen(value)
        }}
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={updateGovernmentAllocationApprovedValueIsPending}
        disabled={updateGovernmentAllocationApprovedValueIsPending}
        title="Ajustar valor aprovado"
        footer={updateGovernmentAllocationApprovedValueIsPending ? 'Ajustando valor' : 'Ajustar valor'}
        body={
          <div className="flex flex-col gap-4">
            <GovernmentAllocationInfoItem
              label="Valor do empenho"
              className="text-white-800"
              value={formatCurrency(Number(companyGovernmentAllocation.total_value))}
            />

            <div className="flex gap-4">
              <GovernmentAllocationInfoItem
                label="Valor aprovado"
                className="text-white-800"
                value={formatCurrency(Number(companyGovernmentAllocation.approved_value))}
              />

              <GovernmentAllocationInfoItem
                label="Valor total consumido"
                className="text-yellow-400"
                value={formatCurrency(Number(companyGovernmentAllocation.consumed_total))}
              />

              <GovernmentAllocationInfoItem
                label="Valor disponível"
                className="text-valid"
                value={formatCurrency(Number(companyGovernmentAllocation.remaining_total))}
              />
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <CurrencyTextField
                label="Novo valor aprovado"
                markAsRequired
                allowZero
                {...register('approved_value')}
                className="text-white-800"
                errorMessage={errors.approved_value?.message || undefined}
              />
            </form>
          </div>
        }
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={updateGovernmentAllocationApprovedValueIsError}
        />
      )}
    </>
  )
}
